import React, { useState,useRef } from 'react';
import styles from './SWPCalculator.module.css';
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import PdfLayout from "./PDF/Layout";
import ResultCard from "./SWPCalculatorResult";
import { FaFilePdf } from "react-icons/fa";

export default function SWPCalculator() {
   
  const pdfRef = useRef(null);
  // Helper to generate ticks at 1 Cr steps, capped at 10 labels
  const generateTicks = (chartData) => {
    const minValue = 0;
    const maxValue = Math.max(
      ...chartData.map(d => d.runningResidual),
      ...chartData.map(d => d.swpAmount)
    );

    const step = 10000000; // 1 Cr in rupees
    const ticks = [];

    for (let v = minValue; v <= maxValue; v += step) {
      ticks.push(v);
    }

    // If more than 10 ticks, thin them out evenly
    if (ticks.length > 10) {
      const interval = Math.ceil(ticks.length / 10);
      return ticks.filter((_, i) => i % interval === 0);
    }

    return ticks;
  };

  const [inputs, setInputs] = useState({
    totalAmount: "",
    monthlyExpense: "",
    yearlyExpense: "",
    assetAllocationDebt: "",
    assetAllocationEquity: "",
    expectedEquityReturns:  "",
    expectedDebtReturns: "",
    yearlyExpenseInflation: "",
    withdrawalRate: ""
  });

  const [placeholders, setPlaceholders] = useState({
    totalAmount: "e.g. 50000000",
    monthlyExpense: "e.g. 500000",
    yearlyExpense: "e.g. 6000000",
    assetAllocationDebt: "e.g. 20",
    assetAllocationEquity: "e.g. 80",
    expectedEquityReturns: "e.g. 15",
    expectedDebtReturns: "e.g. 6",
    yearlyExpenseInflation: "e.g. 5",
    withdrawalRate: "e.g. 12"
  });

  const handleDownloadPdf = async () => {
    const element = pdfRef.current;

    const canvas = await html2canvas(element, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
    });

    const imgData  = canvas.toDataURL("image/png");
    const pdf      = new jsPDF("p", "mm", "a4");

    const pageWidth  = 210 //pdf.internal.pageSize.getWidth();   // 210 mm
    const pageHeight = 297 //pdf.internal.pageSize.getHeight();  // 297 mm
    const footerH    = 20;                                 // 20 mm reserved for footer
    const usable     = pageHeight - footerH;               // 277 mm of content per page

    const imgWidth  = pageWidth;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    const totalPages = Math.ceil(imgHeight / usable);

    for (let p = 0; p < totalPages; p++) {
      if (p > 0) pdf.addPage();

      // Shift image up so page p's content starts at y = 0
      pdf.addImage(imgData, "PNG", 0, -(p * usable), imgWidth, imgHeight);

      // White mask over footer strip — prevents image bleed into footer zone
      pdf.setFillColor(255, 255, 255);
      pdf.rect(0, usable, pageWidth, footerH + 2, "F");

      addFooter(pdf, p + 1, totalPages);
    }

    pdf.save("SWP-CALCULATOR-REPORT.pdf");
  };

  // No longer async — nothing to await
  const addFooter = (pdf, pageNumber, totalPages) => {
          const pageWidth  = pdf.internal.pageSize.getWidth();
          const pageHeight = pdf.internal.pageSize.getHeight();

          const marginX = 15;
          const yText = pageHeight - 10;

          pdf.setFontSize(9);
          pdf.setTextColor(51, 51, 51);

          const baseX = pageWidth - marginX;

          // Text pieces
          const text1 = "Page ";
          const text2 = `${pageNumber}`;
          const text3 = " of ";
          const text4 = `${totalPages}`;

          // measure widths to place correctly
          const w1 = pdf.getTextWidth(text1);
          const w2 = pdf.getTextWidth(text2);
          const w3 = pdf.getTextWidth(text3);

          // start position (right aligned)
          let x = baseX;

          // draw last number (bold)
          pdf.setFont(undefined, "bold");
          pdf.text(text4, x, yText, { align: "right" });
          x -= pdf.getTextWidth(text4);

          // " of "
          pdf.setFont(undefined, "normal");
          pdf.text(text3, x, yText, { align: "right" });
          x -= w3;

          // pageNumber (bold)
          pdf.setFont(undefined, "bold");
          pdf.text(text2, x, yText, { align: "right" });
          x -= w2;

          // "Page "
          pdf.setFont(undefined, "normal");
          pdf.text(text1, x, yText, { align: "right" });
  };
  const [results, setResults] = useState(null);
  const [error, setError] = useState("");

  const handleInputChange = (field, value) => {

    let autoCalcWithdrawalRate;

    setInputs(prev => ({ ...prev, [field]: parseFloat(value) }));

    if ( field == "totalAmount" ){

      autoCalcWithdrawalRate = ( parseFloat( inputs['yearlyExpense'] / value ) == 0 ) ? '' : parseFloat( inputs['yearlyExpense'] / value );
      
      autoCalcWithdrawalRate = (autoCalcWithdrawalRate * 100).toFixed(1);

      if (autoCalcWithdrawalRate == 0 )
        autoCalcWithdrawalRate = "";

      setInputs(prev => ({ ...prev, ['withdrawalRate']: autoCalcWithdrawalRate }));
    }  

    if ( field == "monthlyExpense"){

      let autoCalcYearlyExpenses = ( parseFloat( value * 12 ) == 0 ) ? '' : parseFloat( value * 12 );
     
      if (autoCalcYearlyExpenses == 0 )
        autoCalcYearlyExpenses = "";

      setInputs(prev => ({ ...prev, ['yearlyExpense']: autoCalcYearlyExpenses }));

      autoCalcWithdrawalRate = ( parseFloat( ( value * 12 ) /inputs['totalAmount'] ) == 0 ) ? '' : parseFloat( (value * 12) /inputs['totalAmount'] );

      autoCalcWithdrawalRate = (autoCalcWithdrawalRate * 100).toFixed(1);

      if (autoCalcWithdrawalRate == 0 )
        autoCalcWithdrawalRate = "";

      setInputs(prev => ({ ...prev, ['withdrawalRate']: autoCalcWithdrawalRate }));
    } 
    
  };

  const calculateSWP = () => {
    const {
      totalAmount,
      yearlyExpense,
      assetAllocationDebt,
      assetAllocationEquity,
      expectedEquityReturns,
      expectedDebtReturns,
      yearlyExpenseInflation,
    } = inputs;

    ////////////

    setError("");

    // Input validation
    if (!totalAmount || totalAmount <= 0) {
      setError('Please enter a valid Total Amount greater than 0');
      return;
    }
    if (!yearlyExpense || yearlyExpense <= 0) {
      setError('Please enter a valid Yearly Expense greater than 0');
      return;
    }
    if (!assetAllocationDebt && assetAllocationDebt !== 0) {
      setError('Please enter Asset Allocation Debt %');
      return;
    }
    if (assetAllocationDebt < 0 || assetAllocationDebt > 100) {
      setError('Asset Allocation Debt % must be between 0 and 100');
      return;
    }
    if (!assetAllocationEquity && assetAllocationEquity !== 0) {
      setError('Please enter Asset Allocation Equity %');
      return;
    }
    if (assetAllocationEquity < 0 || assetAllocationEquity > 100) {
      setError('Asset Allocation Equity % must be between 0 and 100');
      return;
    }
    if (Math.abs((assetAllocationDebt + assetAllocationEquity) - 100) > 0.01) {
      setError('Asset Allocation Debt % and Equity % must add up to 100');
      return;
    }
    if (!expectedEquityReturns && expectedEquityReturns !== 0) {
      setError('Please enter Expected Equity Returns %');
      return;
    }
    if (expectedEquityReturns < 0 || expectedEquityReturns > 100) {
      setError('Expected Equity Returns % must be between 0 and 100');
      return;
    }
    if (!expectedDebtReturns && expectedDebtReturns !== 0) {
      setError('Please enter Expected Debt Returns %');
      return;
    }
    if (expectedDebtReturns < 0 || expectedDebtReturns > 20) {
      setError('Expected Debt Returns % must be between 0 and 20');
      return;
    }
    if (!yearlyExpenseInflation && yearlyExpenseInflation !== 0) {
      setError('Please enter Yearly Expense Inflation %');
      return;
    }
    if (yearlyExpenseInflation < 0 || yearlyExpenseInflation > 30) {
      setError('Yearly Expense Inflation % must be between 0 and 30');
      return;
    }

    ////////////

    let yearlyData = [];

    let F = totalAmount * (assetAllocationDebt / 100);
    let H = totalAmount * (assetAllocationEquity / 100);
    let K = -yearlyExpense;
    let swpAmount = K;

    for (let year = 1; year <= 40; year++) {
      const F_current = year === 1 ? F : yearlyData[year - 2].residualDebt;
      const H_current = year === 1 ? H : yearlyData[year - 2].residualEquity;

      const G = F_current * (1 + expectedDebtReturns / 100);
      const I = H_current * (1 + expectedEquityReturns / 100);

      if (year > 1) {
        swpAmount = swpAmount * (1 + yearlyExpenseInflation / 100);
      }

      const L = G > -(swpAmount * assetAllocationDebt / 100)
        ? swpAmount * assetAllocationDebt / 100
        : 0;

      const N = L < 0
        ? swpAmount * assetAllocationEquity / 100
        : swpAmount;

      const residualDebt = L < 0 ? G + L : 0;
      const residualEquity = L < 0 ? I + N : I + N + G;
      const totalResidual = residualDebt + residualEquity;

      const increment = (G - F_current) + (I - H_current) + swpAmount;

      yearlyData.push({
        year,
        startingDebtAllocation: F_current,
        startingEquityAllocation: H_current,
        yearEndGrossTotal: G + I,
        swpAmount: swpAmount,
        residualDebt,
        residualEquity,
        totalResidual,
        increment
      });
    }

    setResults(yearlyData);
  };

  const chartData = results
    ? results.map(row => ({
      year: row.year,
      runningResidual: row.totalResidual,
      swpAmount: row.swpAmount
    }))
    : [];

  return (
    <div className={styles.container}>
      <div className={styles.wrapper}>
        <div className={styles.card}>
          <h1 className={styles.title}>SWP Calculator</h1>

          <div className={styles.formSection}>
            {[
              ['TOTAL AMOUNT IN FUNDS', 'totalAmount'],
              ['MONTHLY EXPENSES', 'monthlyExpense'],
              ['YEARLY EXPENSES (12 MONTHS)', 'yearlyExpense'],
              ['ASSET ALLOCATION DEBT %', 'assetAllocationDebt'],
              ['ASSET ALLOCATION EQUITY %', 'assetAllocationEquity'],
              ['EXPECTED EQUITY RETURNS %', 'expectedEquityReturns'],
              ['EXPECTED DEBT RETURNS %', 'expectedDebtReturns'],
              ['YEARLY EXPENSE INFLATION %', 'yearlyExpenseInflation'],
              ['WITHDRAWAL % PER YEAR', 'withdrawalRate']
            ].map(([label, key]) => (
              <div className={styles.inputGroup} key={key}>
                <label className={styles.label}>{label}</label>
                <input
                  type="number"
                  value={inputs[key]}
                  disabled={ key == "yearlyExpense" || key =="withdrawalRate" ? true : false }
                  onChange={e => handleInputChange(key, e.target.value)}
                  className={styles.input}
                  placeholder={placeholders[key]}
                />
              </div>
            ))}
          </div>

          <button onClick={calculateSWP} className={styles.button}>
            Calculate
          </button>

          {error && <div className={styles.error}>{error}</div>}

          {results && (
          <>
          <div className={styles.cardHeader}  >
              
                <div
                  className={styles.pdfButton}
                  onClick={handleDownloadPdf}
                  title="Download PDF"
                >
                    Export <FaFilePdf size={20} color="#d32f2f" />
                </div>
          </div>
          <ResultCard results={results}  />
          </>  
          )}

          {results  && (
          <PdfLayout ref={pdfRef} type="swp" 
            form={inputs}  
            resultCard={results}   >
          </PdfLayout>
          )}

        </div>
      </div>
    </div>
  );
}
